<template>
	<div class="travel-details">
		<TravelDateAttribute
			class="travel-details__date"
			:icon="['fa-solid', 'fa-plane']"
			:text="formattedDate"
			:icon-class="{ 'is-inbound': inbound }"
		/>
		<div
			v-if="flightData"
			class="travel-details__departure"
		>
			<div class="travel-details__airport-info">
				<span class="travel-details__airport-name">{{ flightData.departureAirport }}{{ NBSP }}</span>
			</div>
			<div class="travel-details__departure-time">
				<time
					v-if="flightData.departureTime"
					class="travel-details__flight-departure-time"
					:datetime="flightData.departureTime?.toString()"
					v-text="flightData.departureTime"
				/>
				<span v-else>--</span>
				&ndash;
				<time
					v-if="flightData.arrivalTime"
					class="travel-details__flight-arrival-time"
					:datetime="flightData.arrivalDate?.toString()"
				>
					{{ flightData.arrivalTime }}
				</time>
				<span v-else> -- </span>
				<span class="travel-details__update-date">{{ increaseDate(departureDate, arrivalDate) }}</span>
			</div>

			<div
				v-show="flightData.directness"
				class="travel-details__directness"
			>
				{{ flightData.directness }}
			</div>
			<div class="travel-details__airline">
				{{ flightData.airline }}
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { formatDateWeekdayPoint } from '@utils/dateTimeUtils';
import type { VerifiedFlightSegment, UnverifiedFlight } from '@interfaces/offer';
import { increaseDate } from '@utils/dateTimeUtils';
import { computed } from 'vue';
import { NBSP } from '@global-js/constants';
import TravelDateAttribute from '@lmt-rpb/Atoms/TravelDateAttribute/TravelDateAttribute.vue';
import { useTranslations } from '../../composables/translations';

interface Props {
	flightSegments?: VerifiedFlightSegment[];
	flight?: UnverifiedFlight;
	inbound?: boolean;
	unverifiedDate?: string | Date;
}

const props = withDefaults(defineProps<Props>(), {
	flightSegments: () => [],
	flight: undefined,
	inbound: false,
	unverifiedDate: '',
});

// Handle verified flight segments
const firstFlightSegment = computed(() => props.flightSegments?.[0]);
const { translate } = useTranslations();
const formattedDate = computed((): string => {
	return formatDateWeekdayPoint(departureDate.value);
});

const lastFlightSegment = computed(() => props.flightSegments?.[props.flightSegments.length - 1]);

const stops = computed((): number => {
	if (props.flightSegments && props.flightSegments.length > 0) {
		return props.flightSegments.length - 1;
	}
	if (props.flight?.NumberOfStops !== undefined) {
		return props.flight.NumberOfStops;
	}
	return 0;
});

const directness = computed((): string => {
	if (props.flight?.DirectFlightAssumed) {
		return translate('offer.flight.stops', 0);
	}
	return translate('offer.flight.stops', { stop: stops.value }, stops.value);
});

const unverifiedDirectness = computed(() => {
	if (props.flight?.DirectFlightAssumed && props.flight.NumberOfStops === 0) {
		return translate('offer.flight.stops', 0);
	}
	if (props.flight?.NumberOfStops && props.flight?.NumberOfStops > 0 && !props.flight?.DirectFlightAssumed) {
		return translate('offer.flight.stops', { stop: props.flight?.NumberOfStops }, props.flight?.NumberOfStops);
	}
	return '';
});

const departureDate = computed((): string | Date => {
	if (firstFlightSegment.value?.Departure?.Date) {
		return firstFlightSegment.value.Departure.Date;
	}
	if (props.flight?.Departure?.Date) {
		return props.flight.Departure.Date;
	}
	return props.unverifiedDate;
});

// Unified flight data for template
const flightData = computed(() => {
	if (!firstFlightSegment.value && !props.flight) {
		return null;
	}

	// For verified flight segments
	if (firstFlightSegment.value && lastFlightSegment.value) {
		return {
			departureAirport: firstFlightSegment.value.Departure.Airport,
			departureTime: firstFlightSegment.value.Departure.time,
			arrivalTime: lastFlightSegment.value.Arrival.time,
			arrivalDate: lastFlightSegment.value.Arrival.Date,
			directness: directness.value,
			airline: firstFlightSegment.value.Airline,
		};
	}

	// For unverified flight
	if (props.flight) {
		return {
			departureAirport: props.flight.Departure.Airport,
			departureTime: props.flight.Departure.time,
			arrivalTime: props.flight.Arrival.time,
			arrivalDate: props.flight.Arrival.Date,
			directness: unverifiedDirectness.value,
			airline: props.flight.Airline?.Name,
		};
	}

	return null;
});

const arrivalDate = computed((): string | Date => {
	return lastFlightSegment?.value?.Arrival?.Date || props.flight?.Arrival?.Date || props.unverifiedDate;
});
</script>

<style lang="scss" scoped>
.travel-details {
	display: flex;
	flex-direction: column;
	width: 100%;

	@media (min-width: $breakpoint-mobilelarge) {
		width: auto;
	}

	&__date {
		margin-bottom: 0.8rem;

		:deep(.is-inbound) {
			transform: rotate(180deg);
		}
	}

	&__directness {
		font-size: $font-small-2;
		font-weight: $font-weight-regular;
		color: $color-placeholder-text;
	}

	.travel-details__departure {
		margin-top: 0.5rem;
	}

	.travel-details__airport-info {
		margin-bottom: 0.5rem;
		display: flex;
		flex-flow: column wrap;

		@media (min-width: $breakpoint-mobiletabs) {
			flex-direction: row;
		}
	}

	.travel-details__airport-name {
		font-weight: $font-weight-semibold;
		color: $color-text;
		line-height: 2.2rem;
	}

	.travel-details__departure-time {
		margin: 1rem 0 0.5rem;
		font-weight: $font-weight-semibold;
		color: $color-text;
	}

	&__airline {
		padding-top: 1rem;
		font-weight: $font-weight-semibold;
		font-size: $font-small-2;
		color: $color-text;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		width: 100%;
		display: block;
		max-width: 13rem;

		@media (min-width: $breakpoint-mobiletabs) {
			max-width: 18rem;
		}

		@media (min-width: $breakpoint-verysmall) {
			max-width: 25rem;
			margin-top: 1.6rem;
		}

		@media (min-width: $breakpoint-small) {
			font-size: $font-small-1;
			text-overflow: inherit;
			white-space: inherit;
			max-width: 16rem;
		}
	}

	&__update-date {
		font-size: $font-small-3;
		line-height: 1.4;
		vertical-align: super;
		margin-left: 0.4rem;
	}
}
</style>
